import React from 'react'
import { Button, FormControlLabel, Grid, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Switch, { SwitchProps } from '@mui/material/Switch'
import { useAppDispatch, useAppSelector } from '../../redux/storeExports'
import {
  AuthenticationActions,
  getPreferredMfa,
  selectPreferredMfa,
} from 'shared/redux/authentication/authenticationSlice'
import { useAuthContext } from 'shared/context/AuthContext'
import {
  getCurrentAuthenticatedUser,
  setPreferredMfa,
} from 'shared/services/AuthenticationService'
import { useNavigate } from 'react-router-dom'
import { store } from '../../redux/store'
import { Toast } from '../toast/toast'

const styles = {
  heading: {
    color: '#3A155A',
    fontFamily: 'InterMedium',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '27px',
    textTransform: 'uppercase',
  },
  label: {
    fontFamily: 'InterRegular',
    fontWeight: 400,
    fontSize: '14px',
    color: '#6B7280',
  },
  saveAuthentication: {
    width: {xs: '100%'},
    maxWidth: {xs:'152px', md: '195px'},
    height: 50,
    marginTop: {xs: '14px', md: 0},
    borderRadius: '10px',
    fontSize: {xs: '14px'}, 
    fontFamily: 'InterMedium',
    fontWeight: 500,
    background: '#9135E0',
    color: '#fff',
    '&.Mui-disabled': {
      background: '#9135E0',
      color: '#fff',
    },
    ':hover': {
      background: '#9135E0',
      color: '#fff',
    },
  },
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 35,
  height: 17,
  padding: 0,
  marginInline: 10,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '400ms',
    '&.Mui-checked': {
      transform: 'translateX(24px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#9135E0' : '#9135E0',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 21,
    height: 21,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E2E8F0' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}))

export const MfaCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authContext = useAuthContext()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      AuthenticationActions.setDraftMfa(
        event.target.checked ? 'SOFTWARE_TOKEN_MFA' : 'NOMFA',
      ),
    )
  }
  const { draftPreferredMfa, registeredPreferredMfa } =
    useAppSelector(selectPreferredMfa)
  const enableTwoFactor = draftPreferredMfa !== 'NOMFA'

  const onTwoFactorSubmit = async () => {
    authContext.setUser(await getCurrentAuthenticatedUser())
    if (draftPreferredMfa === 'SOFTWARE_TOKEN_MFA') {
      navigate('/totp', { state: { from: location.pathname } })
    } else {
      setPreferredMfa('NOMFA').then(() => {
        store.dispatch(getPreferredMfa())
        Toast.show({
          type: 'success',
          text1: 'Your MFA settings have been saved',
        })
      })
    }
  }

  return (
    <Grid item xs={12}>
      <Typography sx={styles.heading}>Authentication</Typography>
      
      <Grid container columnSpacing={3.5} alignItems={'center'} sx={{ marginTop: 2 }}>
        <Grid item xs={12} md={9} lg={10.2} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControlLabel
            control={
              <IOSSwitch 
                checked={enableTwoFactor} 
                onChange={handleChange} 
                sx={{
                  width: 49,
                  height: 26,
                  borderRadius: 13
                }}
                />
            }
            label={
            <Typography
              sx={{
                fontFamily: 'InterRegular',
                fontWeight: 400,
                fontSize: '14px',
                color: '#6B7280',
              }}
              >
              Two Factor Authentication
            </Typography>
            }
            sx={{
              width: {xs:'100%', md: '360px'},
              height: '50px',
              paddingLeft: '5px',
              paddingRight: '40px',
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              margin: 0,
              opacity: 1,
              backgroundColor: '#FFF',
            }}
          />
          <Typography sx={{...styles.label}}>
            Use your mobile authenticator app to generate one-time passcode
            code
          </Typography>
        </Grid>
        <Grid item xs={12} md={3} lg={1.8}  sx={{ display: 'flex', justifyContent:{xs: 'flex-start', md: 'flex-end'} }}>
          <Button
            sx={styles.saveAuthentication}
            onClick={onTwoFactorSubmit}
            disabled={draftPreferredMfa === registeredPreferredMfa}>
            Save Changes
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}
